import {AvailableQuoteActions} from 'Quoting/models/DTOs/Opportunity';
import {isDefined} from 'Quoting/utils/utilities';
import {head} from 'fp-ts/lib/Array';
import {Eq} from 'fp-ts/lib/Eq';
import {fold, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ActionItem} from './QuoteActionMenu';

export const isActionApplicable = (actionName: string, availableActions: AvailableQuoteActions[]) =>
  Array.isArray(availableActions) && availableActions.length > 0
    ? pipe(
        availableActions,
        head,
        fold(
          () => false,
          ({availableCommands, availableEndpoints, availableFlagCommands}) =>
            pipe(
              fromNullable(availableCommands),
              fold(
                () => false,
                value => value.map(ac => ac.command).includes(actionName)
              )
            ) ||
            pipe(
              fromNullable(availableEndpoints),
              fold(
                () => false,
                value => value.map(ac => ac.endpoint).includes(actionName)
              )
            ) ||
            pipe(
              fromNullable(availableFlagCommands),
              fold(
                () => false,
                value => value.map(ac => ac.command).includes(actionName)
              )
            )
        )
      )
    : false;

export const getApplicableActions = (availableActions: AvailableQuoteActions[]): ActionItem[] =>
  pipe(
    Object.values(QuoteListAction),
    v => v.filter(action => action !== QuoteListAction.Propose),
    v =>
      v.map(action =>
        isActionApplicable(action, availableActions)
          ? {actionName: action, actionLabel: quoteActionLabelMapping.get(action as QuoteListAction) ?? ''}
          : undefined
      ),
    v => v.filter(isDefined),
    v =>
      isActionApplicable(QuoteListAction.Propose, availableActions)
        ? v.concat({
            actionName: QuoteListAction.CreateProposal,
            actionLabel: quoteActionLabelMapping.get(QuoteListAction.CreateProposal) ?? '',
          })
        : v,
    sortActions
  );

const eqActionItem: Eq<ActionItem> = {
  equals: (x, y) => x.actionName === y.actionName && x.actionLabel === y.actionLabel,
};

export const getCommonActionsForSelectedQuotes = (actions: AvailableQuoteActions[][], numberOfSelectedQuotes: number) =>
  pipe(
    actions ?? [],
    v => v.map(action => getApplicableActions(action)),
    v =>
      v.reduce(
        (acc, current) => acc.filter(item => current.some(currentItem => eqActionItem.equals(item, currentItem))),
        v[0]
      ),
    v => (numberOfSelectedQuotes > 1 ? v.filter(action => action.actionName !== QuoteListAction.CopyQuote) : v),
    sortActions
  );

const sortActions = (actions: ActionItem[]) => [...actions].sort((a, b) => a.actionLabel.localeCompare(b.actionLabel));

export enum QuoteListAction {
  PrepareForProposal = 'prepareForProposal',
  CreateProposal = 'createProposal',
  DeclineByCompany = 'companyDecline',
  DeclineByCustomer = 'customerDecline',
  CopyQuote = 'quoteCopy',
  Propose = 'propose',
}

export const quoteActionLabelMapping = new Map([
  [QuoteListAction.PrepareForProposal, 'Ready to Propose'],
  [QuoteListAction.CreateProposal, 'Create Proposal'],
  [QuoteListAction.DeclineByCompany, 'Decline by SIC'],
  [QuoteListAction.DeclineByCustomer, 'Decline by Broker/Group'],
  [QuoteListAction.CopyQuote, 'Copy from Quote'],
  // [QuoteListAction.Propose, 'Propose'],
  // [QuoteListAction.ReferToUnderwriting, 'Refer to Underwriting'],
  // [QuoteListAction.Issue, 'Issue'],
  // [QuoteListAction.IssueWithoutEISBilling, 'Issue w/o EIS Billing'],
  // [QuoteListAction.MarkAsSold, 'Mark as Sold'],
]);
